/**
 * Spanish Locale Formatters
 * All monetary amounts and dates must follow Spanish locale format
 * Money: 82.650,00 € (thousands separator + two decimals + trailing symbol)
 * Dates: DD/MM/YYYY (13/04/2026)
 */

/**
 * Format number as currency in Spanish locale
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string (e.g., "82.650,00 €")
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '0,00 €';
  }
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0,00 €';
  }
  
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Format date in Spanish locale (DD/MM/YYYY)
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date string (e.g., "13/04/2026")
 */
export const formatDate = (date) => {
  if (!date) {
    return '';
  }
  
  let dateObj;
  
  // Handle moment objects
  if (date && typeof date === 'object' && date._isAMomentObject) {
    dateObj = date.toDate();
  }
  // Handle string dates
  else if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  // Handle Date objects
  else if (date instanceof Date) {
    dateObj = date;
  }
  else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format date with time in Spanish locale (DD/MM/YYYY HH:mm)
 * @param {Date|string|moment} date - The date to format
 * @returns {string} Formatted date string with time (e.g., "13/04/2026 14:30")
 */
export const formatDateTime = (date) => {
  if (!date) {
    return '';
  }
  
  let dateObj;
  
  // Handle moment objects
  if (date && typeof date === 'object' && date._isAMomentObject) {
    dateObj = date.toDate();
  }
  // Handle string dates
  else if (typeof date === 'string') {
    dateObj = new Date(date);
  }
  // Handle Date objects
  else if (date instanceof Date) {
    dateObj = date;
  }
  else {
    return '';
  }
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};


/**
 * Translate status labels based on current language via i18next
 * Uses translation keys: status_Approved, status_Pending, status_Cancelled, etc.
 * @param {string} status - The status string (Approved, Pending, Cancelled, Verified, etc.)
 * @returns {string} Translated status string from i18next
 */
export const formatStatus = (status) => {
  if (!status) return '';
  
  // Use dynamic import to avoid circular deps - i18next must be imported in calling file
  // We use a simple lookup since i18next is not available here
  const lang = localStorage.getItem('I18N_LANGUAGE');
  
  if (lang === 'sp') {
    const statusMap = {
      'Approved': 'Confirmado',
      'Pending': 'Pendiente',
      'Cancelled': 'Cancelado',
      'Rejected': 'Rechazado',
      'Verified': 'Verificado',
      'Enable': 'Activo',
      'Disable': 'Inactivo',
    };
    return statusMap[status] || status;
  }
  
  // English or any other language - return as-is
  return status;
};


/**
 * Translate fiat_transaction description based on selected language
 * Descriptions are stored in English in the DB. This function translates them
 * to Spanish on the frontend when the user's language is set to 'sp'.
 * 
 * @param {string} description - The English description from the DB
 * @param {object} [element] - Optional: the full transaction element with property_name_sp
 * @returns {string} Translated description if Spanish is selected, otherwise original
 */
export const translateDescription = (description, element, propertyNameMap) => {
  if (!description) return '';
  
  const lang = localStorage.getItem('I18N_LANGUAGE');
  
  // Strip email addresses from descriptions (applies to all languages)
  const stripped = description.replace(/\s*\([^)]*@[^)]*\)/, '');
  
  if (lang !== 'sp') return stripped;
  
  // Exact matches
  const exactMap = {
    'Welcome Bonus': 'Bono de bienvenida',
    'Invested': 'Invertido',
    'Claimed': 'Reclamado',
    'Withdraw': 'Retirada',
    'Manually Added': 'Añadido manualmente',
    'Gift Card Redeemption': 'Canje de tarjeta regalo',
    'Early Bird': 'Bono Early Bird',
    'Credit': 'Crédito',
    'Debit': 'Débito',
    'euro-wallet': 'Monedero Euro',
    'Euro Monedero': 'Monedero Euro',
    'Euro monedero': 'Monedero Euro',
    'bank-transfer': 'Transferencia bancaria',
    'wire-transfer': 'Transferencia bancaria',
    'Wire Transfer Euro': 'Transferencia bancaria',
  };
  
  if (exactMap[description]) {
    return exactMap[description];
  }
  
  // Pattern matches
  if (description.startsWith('Welcome Bonus from ')) {
    const nameOnly = description.replace('Welcome Bonus from ', '').replace(/\s*\([^)]*\)/, '');
    return 'Bono de bienvenida de ' + nameOnly;
  }
  
  if (description.startsWith('Referral Reward from ')) {
    const nameOnly = description.replace('Referral Reward from ', '').replace(/\s*\([^)]*\)/, '');
    return 'Recompensa por referido de ' + nameOnly;
  }
  
  if (description.startsWith('Dividend of ')) {
    const engName = description.replace('Dividend of ', '');
    // Try to find Spanish name from the map
    if (propertyNameMap) {
      for (const [eng, sp] of Object.entries(propertyNameMap)) {
        if (engName.includes(eng) || eng.includes(engName)) {
          return `Dividendo de ${sp}`;
        }
      }
    }
    const spName = element && (element.property_name_sp || element.property_name);
    if (spName) {
      return `Dividendo de ${spName}`;
    }
    let text = description.replace('Dividend of ', 'Dividendo de ');
    text = text.replace(' Project ', ' ');
    if (!text.includes('Proyecto ')) {
      text = text.replace('Dividendo de ', 'Dividendo de Proyecto ');
    }
    return text;
  }
  
  if (description.endsWith('tokens claimed')) {
    const match = description.match(/(\d+)\s+tokens claimed$/);
    const tokenCount = match ? match[1] : '';
    // Try to find Spanish name from the map by matching English property name in description
    if (propertyNameMap) {
      for (const [eng, sp] of Object.entries(propertyNameMap)) {
        if (description.includes(eng)) {
          return `${sp} ${tokenCount} tokens reclamados`;
        }
      }
    }
    const spName = element && (element.property_name_sp || element.property_name);
    if (spName) {
      return `${spName} ${tokenCount} tokens reclamados`;
    }
    let text = description.replace('tokens claimed', 'tokens reclamados');
    text = text.replace(' Project ', ' ');
    return 'Proyecto ' + text;
  }
  
  if (description.endsWith('tokens reclamados')) {
    const match = description.match(/(\d+)\s+tokens reclamados$/);
    const tokenCount = match ? match[1] : '';
    if (propertyNameMap) {
      for (const [eng, sp] of Object.entries(propertyNameMap)) {
        if (description.includes(eng)) {
          return `${sp} ${tokenCount} tokens reclamados`;
        }
      }
    }
    const spName = element && (element.property_name_sp || element.property_name);
    if (spName) {
      return `${spName} ${tokenCount} tokens reclamados`;
    }
    let text = description.replace(' Project ', ' ');
    if (!text.startsWith('Proyecto ')) {
      return 'Proyecto ' + text;
    }
    return text;
  }
  
  // Fallback — return original
  return description;
};

/**
 * Translate transaction type (Credit/Debit) based on selected language
 * @param {string} type - "Credit" or "Debit"
 * @returns {string} Translated type
 */
export const translateType = (type) => {
  if (!type) return '';
  
  const lang = localStorage.getItem('I18N_LANGUAGE');
  if (lang !== 'sp') return type;
  
  const typeMap = {
    'Credit': 'Crédito',
    'Debit': 'Débito',
  };
  
  return typeMap[type] || type;
};


/**
 * Format number with Spanish locale thousand separators
 * Spanish format: 2.000, 10.653, 1.500.000 (period as thousand separator)
 * @param {number|string} value - The number to format
 * @returns {string} Formatted number string (e.g., "2.000", "10.653")
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  // Manual formatting to ensure thousand separators with periods
  const numStr = Math.floor(numValue).toString();
  const parts = [];
  
  for (let i = numStr.length - 1, j = 0; i >= 0; i--, j++) {
    if (j > 0 && j % 3 === 0) {
      parts.unshift('.');
    }
    parts.unshift(numStr[i]);
  }
  
  return parts.join('');
};
